import { DatePicker } from "$components/inputs";
import { Box, styled } from "@material-ui/core";
import dayjs from "dayjs";
import { FormattedMessage, useIntl } from "react-intl";
import { useDebouncedCallback } from "use-debounce";

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  flexWrap: "wrap",
  "& > *": {
    minWidth: "140px",
  },
}));

const ClearLink = styled("button")(({ theme }) => ({
  background: "none",
  border: "none",
  padding: theme.spacing(0.5, 0),
  fontSize: "0.75rem",
  fontWeight: 500,
  color: theme.palette.primary.main,
  cursor: "pointer",
  fontFamily: "inherit",
  whiteSpace: "nowrap",
  minWidth: 0,
  "&:hover": { textDecoration: "underline" },
}));

interface Props {
  fromDate?: string;
  toDate?: string;
  setFromDate: (value: string) => void;
  setToDate: (value: string) => void;
}

export const PreviousFilters: FC<Props> = ({
  fromDate,
  toDate,
  setFromDate,
  setToDate,
}) => {
  const { formatMessage } = useIntl();

  // The DatePicker fires onChange with `null` when the user clears the field,
  // and dayjs(null) yields "Invalid Date" — guard so we treat that as a clear
  // (delete the param) rather than writing "Invalid Date" into the URL.
  const formatDate = (date: unknown): string => {
    if (!date) return "";
    const parsed = dayjs(date as Parameters<typeof dayjs>[0]);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
  };
  const setFromDebounced = useDebouncedCallback(
    (date) => setFromDate(formatDate(date)),
    800,
  );
  const setToDebounced = useDebouncedCallback(
    (date) => setToDate(formatDate(date)),
    800,
  );

  const hasFilter = Boolean(fromDate || toDate);

  const onClear = () => {
    // Drop any in-flight debounced writes so a pending date from the input
    // box doesn't repopulate the URL after Clear.
    setFromDebounced.cancel();
    setToDebounced.cancel();
    setFromDate("");
    setToDate("");
  };

  return (
    <Container data-testid="previous-filters">
      <DatePicker
        hideHelper
        label={formatMessage({
          id: "invoices.filter.from",
          defaultMessage: "From",
        })}
        inputProps={{ "aria-label": "Change From Date" }}
        onChange={setFromDebounced}
        value={fromDate ?? null}
      />
      <DatePicker
        hideHelper
        label={formatMessage({
          id: "invoices.filter.to",
          defaultMessage: "To",
        })}
        inputProps={{ "aria-label": "Change To Date" }}
        onChange={setToDebounced}
        value={toDate ?? null}
      />
      {hasFilter && (
        <ClearLink
          type="button"
          onClick={onClear}
          data-testid="previous-filters-clear"
        >
          <FormattedMessage
            id="invoicing.v2.filter.clear"
            defaultMessage="Clear"
          />
        </ClearLink>
      )}
    </Container>
  );
};
